import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import Header from "@cloudscape-design/components/header"
import Icon from "@cloudscape-design/components/icon"
import Modal from "@cloudscape-design/components/modal"
import SpaceBetween from "@cloudscape-design/components/space-between"
import Table from "@cloudscape-design/components/table"
import { useState, useRef } from "react"
import type { TestConceptGroup } from "@/generated/sdk"
import {
  parseText,
  classifyColumns,
  type ColumnClassification,
  type ColumnRole,
} from "./parseTestConcepts"

interface Props {
  isOpen: boolean
  onClose: () => void
  onImport: (items: TestConceptGroup[]) => void
}

/**
 * A draggable column chip that can be moved between role groups. Drag-and-drop has no Cloudscape
 * primitive, so the draggable element and its drop-zone visuals are hand-rolled (escape hatch).
 */
function ColumnChip({
  name,
  onDragStart,
  onDragEnd,
}: {
  name: string
  onDragStart: () => void
  onDragEnd: () => void
}) {
  return (
    <div draggable onDragStart={onDragStart} onDragEnd={onDragEnd} style={{ cursor: "grab" }}>
      <Box padding={{ vertical: "xxs", horizontal: "xs" }}>{name}</Box>
    </div>
  )
}

export function TestConceptsImportWizard({ isOpen, onClose, onImport }: Props) {
  const [fileName, setFileName] = useState("")
  const [headers, setHeaders] = useState<string[]>([])
  const [rows, setRows] = useState<string[][]>([])
  const [columns, setColumns] = useState<ColumnClassification[]>([])
  const [dragIdx, setDragIdx] = useState<number | null>(null)
  const [dragOver, setDragOver] = useState<ColumnRole | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const reset = () => {
    setFileName("")
    setHeaders([])
    setRows([])
    setColumns([])
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ""
    const reader = new FileReader()
    reader.onload = () => {
      const { headers: h, rows: r } = parseText(reader.result as string)
      if (h.length === 0) {
        alert("Could not parse file. Ensure it has a header row and data rows.")
        return
      }
      setFileName(file.name)
      setHeaders(h)
      setRows(r)
      setColumns(classifyColumns(h, r))
    }
    reader.readAsText(file)
  }

  const setRole = (idx: number, role: ColumnRole) => {
    setColumns((prev) => prev.map((c, i) => (i === idx ? { ...c, role } : c)))
  }

  const condCols = columns.filter((c) => c.role === "condition").map((c) => c.name)
  const valCols = columns.filter((c) => c.role === "value").map((c) => c.name)
  const allActiveCols = [...condCols, ...valCols]

  const handleImport = () => {
    // Build rows from the raw data, keeping only active columns
    const importRows = rows.map((row) => {
      const obj: Record<string, string> = {}
      for (const col of allActiveCols) {
        const idx = headers.indexOf(col)
        if (idx !== -1) obj[col] = row[idx] || ""
      }
      return obj
    })

    const group: TestConceptGroup = {
      id: crypto.randomUUID(),
      conditionColumns: condCols,
      valueColumns: valCols,
      rows: importRows,
    }
    onImport([group])
    handleClose()
  }

  const dropZoneProps = (role: ColumnRole) => ({
    onDragOver: (e: React.DragEvent) => {
      e.preventDefault()
      setDragOver(role)
    },
    onDragLeave: () => setDragOver(null),
    onDrop: () => {
      if (dragIdx !== null) setRole(dragIdx, role)
      setDragOver(null)
      setDragIdx(null)
    },
  })

  const renderGroup = (role: ColumnRole, title: string) => {
    const inGroup = columns.filter((c) => c.role === role)
    return (
      // Drag-and-drop has no Cloudscape primitive; the drop zone and its "drag over" highlight are
      // hand-rolled (justified escape hatch). Uses a design token so it tracks the active theme.
      <div
        {...dropZoneProps(role)}
        style={{
          border: "1px dashed",
          borderColor:
            dragOver === role
              ? "var(--color-border-item-focused, #0972d3)"
              : "var(--color-border-divider-default, #e9ebed)",
          borderRadius: "var(--border-radius-container, 8px)",
          padding: "var(--space-xs, 8px)",
        }}
      >
        <SpaceBetween size="xxs">
          <Box variant="awsui-key-label">{title}</Box>
          {inGroup.length === 0 ? (
            <Box color="text-status-inactive" fontSize="body-s">
              Drop here
            </Box>
          ) : (
            <SpaceBetween size="xxs">
              {columns.map((col, idx) =>
                col.role !== role ? null : (
                  <ColumnChip
                    key={col.name}
                    name={col.name}
                    onDragStart={() => setDragIdx(idx)}
                    onDragEnd={() => {
                      setDragIdx(null)
                      setDragOver(null)
                    }}
                  />
                ),
              )}
            </SpaceBetween>
          )}
        </SpaceBetween>
      </div>
    )
  }

  const previewHeaders = headers
    .map((h, i) => ({ name: h, index: i, role: columns[i]?.role }))
    .filter((h) => h.role !== "ignore")
  const previewItems = rows.slice(0, 100).map((row, i) => ({ id: String(i), cells: row }))

  // The Cloudscape Modal portals its content into the DOM even when hidden, so guard here to keep
  // the same observable behavior as before: nothing rendered while closed.
  if (!isOpen) return null

  return (
    <>
      {/*
        Hidden native file input. Kept outside the Modal (which portals its content out of the
        component's DOM subtree) so callers/tests can still reach it via the component root. The
        Cloudscape file-input does not fit this "restyled trigger button" flow.
      */}
      <input ref={fileRef} type="file" accept=".csv,.tsv,.txt" onChange={handleFile} hidden />
      <Modal
        visible={isOpen}
        onDismiss={handleClose}
        closeAriaLabel="Close"
        size="max"
        header="Import Test Concepts"
        footer={
          fileName ? (
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={handleClose}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleImport} disabled={valCols.length === 0}>
                  Import {rows.length} rows
                </Button>
              </SpaceBetween>
            </Box>
          ) : undefined
        }
      >
        {!fileName ? (
          <SpaceBetween size="s" direction="horizontal">
            <Box color="text-status-inactive">No file selected</Box>
            <Button iconName="upload" onClick={() => fileRef.current?.click()}>
              Select file
            </Button>
          </SpaceBetween>
        ) : (
          <SpaceBetween size="l">
            <SpaceBetween size="xs" direction="horizontal">
              <Box>
                <Icon name="file" /> {fileName}
              </Box>
              <Button iconName="refresh" onClick={() => fileRef.current?.click()}>
                Change file
              </Button>
              <Box color="text-status-inactive">
                {headers.length} columns, {rows.length} rows
              </Box>
            </SpaceBetween>

            <SpaceBetween size="l" direction="horizontal">
              <SpaceBetween size="m">
                <Box color="text-status-inactive" fontSize="body-s">
                  Drag columns between groups
                </Box>
                {renderGroup("condition", "Condition Columns")}
                {renderGroup("value", "Value Columns")}
                {renderGroup("ignore", "Ignored Columns")}
              </SpaceBetween>

              <SpaceBetween size="xs">
                <Header
                  variant="h3"
                  counter={`(${rows.length} rows · ${condCols.length} conditions · ${valCols.length} value columns)`}
                >
                  Preview
                </Header>
                <Table
                  variant="embedded"
                  columnDefinitions={previewHeaders.map((h) => ({
                    id: h.name,
                    header: h.name,
                    cell: (item: { id: string; cells: string[] }) => item.cells[h.index] || "—",
                  }))}
                  items={previewItems}
                  trackBy="id"
                  empty="No rows"
                />
              </SpaceBetween>
            </SpaceBetween>
          </SpaceBetween>
        )}
      </Modal>
    </>
  )
}
